import DownloadIcon from '@mui/icons-material/Download';
import { Button, IconButton, Tooltip } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { defineMessage, useIntl } from 'react-intl';

const downloadMessage = defineMessage({
  id: 'common.download',
  defaultMessage: 'Descargar',
});

interface DownloadUrlButtonProps {
  /** URL to navigate to when the download starts. */
  readonly url: string;
  /** Time in milliseconds before restoring the button. */
  readonly timeout?: number;
  /** Renders an IconButton with tooltip instead of a labeled Button. */
  readonly iconOnly?: boolean;
  readonly size?: 'small' | 'medium' | 'large';
  readonly color?: 'inherit' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  readonly disabled?: boolean;
}

/**
 * Navigates to a downloadable URL in the same tab and prevents repeated clicks while it loads.
 */
export function DownloadUrlButton({
  url,
  timeout = 1000,
  iconOnly = false,
  size,
  color,
  disabled,
}: DownloadUrlButtonProps) {
  const [loading, setLoading] = useState(false);
  const timeoutRef = useRef<number | null>(null);
  const { $t } = useIntl();

  useEffect(() => {
    setLoading(false);

    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [url]);

  function handleClick() {
    setLoading(true);
    window.location.assign(url);
    timeoutRef.current = window.setTimeout(() => {
      setLoading(false);
      timeoutRef.current = null;
    }, timeout);
  }

  const isDisabled = disabled || loading;
  const label = $t(downloadMessage);

  if (iconOnly) {
    return (
      <Tooltip title={label} placement="top">
        {/* span keeps the tooltip working while the IconButton is disabled */}
        <span>
          <IconButton color={color} size={size} disabled={isDisabled} onClick={handleClick}>
            <DownloadIcon />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  return (
    <Button
      disabled={isDisabled}
      size={size}
      color={color}
      startIcon={<DownloadIcon />}
      onClick={handleClick}
    >
      {label}
    </Button>
  );
}
